import { ComposableElement } from '../../ComposableElement.mjs';

/** 
 * Tablist Web Component
 * 
 * This component represents the "tab list" in a tabbed interface, which contains the individual "tab" elements that users interact with to switch between different "tab panels" (the content associated with each tab). 
 * It's vaguely designed to work in conjunction with a corresponding `ba-tab-panels` component and a `ba-tab` parent (though it IS decoupled and simply publishes a Custom Event `ba-tab-change` on change so has potential for reuse in other use cases).
 * It largely focuses on the interactive portion of making a tabbed interface work.
 */
export class Tablist extends ComposableElement {
    constructor() {
        super();

        this._changeTabFunc = this._changeTab.bind(this);
        this._keyDownHandler = this._keyDown.bind(this);
    }

    // -------------------------------
    // Web Component Lifecycle Methods
    // -------------------------------

    /**
     * Does initial tab setup and adds event listeners for tab interaction.
     * 
     * `connectedCallback` is a lifecycle method in web components that runs when the custom element is inserted into the document's Document Object Model (DOM). 
     * It can be invoked multiple times if the element is removed and then re-inserted into the DOM.
     * 
     * Timing: It is called after the element's constructor() but before the element's children are necessarily connected or fully rendered.
     * Purpose: It is the ideal place to set up tasks that should only occur when the element is actually present in the live document. Common uses include:
     */
    connectedCallback() {
        // Attach the internals to the component to allow for the use of a Declarative Shadow Root
        const internals = this.attachInternals();
        
        // check for a Declarative Shadow Root:
        this.shadow = this.shadowRoot;
        if (!this.shadow) {
            // there wasn't one. create a new Shadow Root:
            this.shadow = this.attachShadow({ mode: 'open' });

            setTimeout(() => {
                // Recreate the template using the shadow DOM that is only available through JavaScript
                this.createTemplateInJS(this.shadow);
            }, 0);
        }

        setTimeout(() => {
            const activeTab = this.shadow.querySelector('[role="tab"].is-active') || this.shadow.querySelector('[role="tab"]');

            if(activeTab == null) {
                throw new Error('No tabs found. Please ensure that your configuration includes at least one tab.');
            }

            // Set the active tab ID to the currently active tab in the DOM (this makes the implementation of `getActiveTab` basically trivial)
            this.activeTabId = activeTab.getAttribute('id');

            // Ensure it's visually active if we fell back to the first one
            if (!activeTab.classList.contains('is-active')) {
                this.switchTab(activeTab);
            }

            this.shadow.querySelectorAll('[role="tab"]').forEach((tab) => {
                // Add event listeners for click and keydown (for accessibility) to each tab element.
                // The event listener for click will change the active tab and show the corresponding tab panel.
                tab.addEventListener('click', this._changeTabFunc);
                tab.addEventListener('keydown', this._keyDownHandler);
            });
        }, 0);
    }

    /** Cleanup when the element is removed from the DOM */
    disconnectedCallback() {
        // Clean up any event listeners or resources when the element is removed from the DOM
        this.shadow.querySelectorAll('[role="tab"]').forEach((tab) => {
            try {
                tab.removeEventListener('click', this._changeTabFunc);
                tab.removeEventListener('keydown', this._keyDownHandler);
            }
            catch (error) { 
                /* Do Nothing (Ignore errors that occur here because any number of things could cause errors during the cleanup process and we don't want to throw errors just because something went wrong during cleanup) */ 
                console.log(`An error occured during disconnect:`, error);
            }
        });
    }

    /**
     * Recreate the template in the shadow DOM through JavaScript instead of relying on the `shadowrootmode` attribute
     * 
     * @param {ShadowRoot} shadow The shadow DOM to attach the template to
     */
    createTemplateInJS(shadow) {
        const config = this.initializeComponent('tablist', shadow, { stylesAndScriptsNameOverride: 'tabs' });
        const { tabsId, tabsStyleClasses, tabsAriaLabel, tabsMeta } = config;
        
        // Create the nav ("tab list") element
        const navElem = document.createElement('nav'); 
        navElem.setAttribute('role', 'tablist');
        navElem.id = tabsId;
        navElem.classList.add('tabs');
        tabsStyleClasses.forEach(cls => navElem.classList.add(cls)); 
        navElem.setAttribute('aria-label', tabsAriaLabel);
        navElem.dataset.tabs = '';
        
        // Generate Tabs (button elements)
        tabsMeta.forEach((tab, index) => {
            // Create the tab button element
            const tabBtnElem = document.createElement('button'); 
            tabBtnElem.setAttribute('role', 'tab');
            tabBtnElem.id = `tab-${index}`;
            tabBtnElem.classList.add('tabs-title');
            if(tab.active) {
                tabBtnElem.classList.add('is-active');
            } 
            tabBtnElem.setAttribute('aria-controls', tab.id); 
            tabBtnElem.setAttribute('aria-selected', tab.active ? 'true' : 'false');
            tabBtnElem.tabIndex = tab.active ? 0 : -1;

            // Create a slot element for the tab button's label/content
            const tabSlotElem = document.createElement('slot'); 
            tabSlotElem.name = `tab-${index}`;

            // Append the slot element to the tab button element
            tabBtnElem.appendChild(tabSlotElem);

            // Append the tab button element ("the tab") to the nav element ("the tab list")
            navElem.appendChild(tabBtnElem);
        });

        // Finally, append the entire tabs container div to the shadow DOM of the component
        shadow.appendChild(navElem);
    }

    // --------------
    // Helper Methods
    // --------------

    /**
     * Get the currently active tab.
     * 
     * useful for external interactions with the component.
     * 
     * @return {HTMLButtonElement} The currently active tab element
     */
    getActiveTab() {
        const tab = this.shadow.getElementById(this.activeTabId);
        
        return tab;
    }

    /**
     * Switch the active tab to the selected tab.
     * 
     * Note, this is meant to be available both internally to the class but also externally (if JavaScript ever needed to programmatically switch the active tab, they could call this method directly) so it is not prefixed with an underscore like the other internal helper methods.
     * 
     * @param {HTMLElement} selectedTab The tab element to activate
     */
    switchTab(selectedTab) {
        // Update Tabs
        this.shadow.querySelectorAll('[role="tab"]').forEach(tab => {
            // Easy access boolean if is the selected tab or not
            const isSelected = tab === selectedTab;

            // Update the aria-selected attribute and tabindex of each tab based on whether it is the selected tab or not
            tab.setAttribute('aria-selected', isSelected);
            tab.setAttribute('tabindex', isSelected ? 0 : -1);

            // Remove the active class from any tab that isn't the selected tab
            if(!isSelected && tab.classList.contains('is-active')) {
                tab.classList.remove('is-active');
            }

            // Add the active class to the selected tab if it doesn't already have it
            if(isSelected && !tab.classList.contains('is-active')) {
                tab.classList.add('is-active');
            }
        });

        // Get the ID of the tab panel that should be shown based on the aria-controls attribute of the selected tab
        const targetId = selectedTab.getAttribute('aria-controls');

        // Show the corresponding tab panel and hide the others by calling the `switchTab` method on the `ba-tab-panels` component and passing it the ID of the tab panel to show (the one that corresponds to the selected tab)
        this.dispatchEvent(new CustomEvent('ba-tab-change', {
            detail: { targetId },
            bubbles: true,
            composed: true
        }));

        this.activeTabId = selectedTab.getAttribute('id');
    }

    // ----------------------
    // Private Event Handlers
    // ----------------------

    /**
     * Internal change event handler for when a tab is clicked or activated via keyboard. 
     * It prevents the default behavior and then calls the `switchTab` method to switch the active tab to the clicked/selected tab.
     * 
     * @param {Event} e The click/keyboard event triggered by clicking or pressing enter/space on a tab element. The event listener for this is added in the `connectedCallback` method. 
     */
    async _changeTab(e) {
        // Prevent the default behavior
        e.preventDefault();

        if(e.currentTarget.getAttribute('id') === this.activeTabId) {
            // The clicked tab is already active, so we don't need to do anything
            return;
        }

        // Call the switchTab method to switch the active tab to the clicked/selected tab
        this.switchTab(e.currentTarget);
    }

    /**
     * Internal keydown event handler for when a tab is focused and the user presses the left or right arrow keys, Enter or Space key.
     * It prevents the default behavior and then calls the `_changeTab` method to switch the active tab to the focused/selected tab.
     * 
     * @param {KeyboardEvent} e The keyboard event triggered by pressing the left or right arrow keys, Enter or Space on a focused tab element. The event listener for this is added in the `connectedCallback` method.
     */
    async _keyDown(e) {
        if(e.key === 'Enter' || e.key === ' ') {
            await this._changeTab(e);
        }

        // Arrow keys handle "roving focus"
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            
            const tabs = Array.from(this.shadow.querySelectorAll('[role="tab"]'));
            const currentIndex = tabs.indexOf(e.currentTarget);
            let nextIndex;

            if (e.key === 'ArrowRight') {
                // Move right, loop to start if at the end
                nextIndex = (currentIndex + 1) % tabs.length;
            } else if (e.key === 'ArrowLeft') {
                // Move left, loop to end if at the start
                nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
            }

            // Move the physical browser focus to the new tab
            tabs[nextIndex].focus();
        }
    }
}

document.addEventListener('DOMContentLoaded', () => {
    if (!customElements.get('ba-tablist')) {
        customElements.define('ba-tablist', Tablist);
    }
});